import { routes } from "@/router/all_routes";
import type { DemoCapturePage } from "./demo-capture.config";
import { DEMO_CAPTURE_PASSWORD } from "./demo-capture.config";

export type DemoCaptureMode = "full" | "magazine" | "current" | "matrix";

export interface DemoCaptureTourStep {
  profileId: string;
  profileLabel: string;
  email: string;
  password: string;
  page: DemoCapturePage;
  magazineSection?: string;
  magazineHeadline?: string;
}

export interface DemoCaptureTourDefinition {
  id: "full" | "magazine" | "matrix";
  label: string;
  shortLabel: string;
  description: string;
  outputSubdir: string;
  steps: DemoCaptureTourStep[];
}

const PAGE = {
  tableauDeBord: {
    id: "tableau-de-bord",
    label: "Tableau de bord",
    path: routes.tenantDashboard,
    waitMs: 3200,
  },
  admissions: {
    id: "admissions",
    label: "Admissions",
    path: routes.admissions,
    waitMs: 3200,
  },
  patients: {
    id: "patients",
    label: "Liste patients",
    path: routes.patients,
    waitMs: 3200,
  },
  consultations: {
    id: "consultations",
    label: "Consultations",
    path: routes.consultations,
    waitMs: 3200,
  },
  prescriptions: {
    id: "prescriptions",
    label: "Prescriptions",
    path: routes.prescriptions,
    waitMs: 3200,
  },
  ordonnances: {
    id: "ordonnances",
    label: "Ordonnances à traiter",
    path: routes.pharmacyOrders,
    waitMs: 3600,
  },
  dispensation: {
    id: "dispensation",
    label: "Dispensation / facture",
    path: routes.pharmacyInvoice,
    waitMs: 3600,
  },
  stockArticles: {
    id: "stock-articles",
    label: "Stock pharmacie",
    path: routes.pharmacyStockArticles,
    waitMs: 3200,
  },
  assuranceDossiers: {
    id: "assurance-dossiers",
    label: "Assurance — dossiers",
    path: routes.insuranceCases,
    waitMs: 3200,
  },
  caisse: {
    id: "caisse",
    label: "Caisse",
    path: routes.billingCashier,
    waitMs: 3200,
  },
  laboratoire: {
    id: "laboratoire",
    label: "Laboratoire",
    path: routes.laboratory,
    waitMs: 3000,
  },
  comptabilite: {
    id: "comptabilite",
    label: "Comptabilité",
    path: routes.billingAccounting,
    waitMs: 3200,
  },
} as const satisfies Record<string, DemoCapturePage>;

function step(
  profileId: string,
  profileLabel: string,
  email: string,
  page: DemoCapturePage,
  magazine?: { section: string; headline: string },
): DemoCaptureTourStep {
  return {
    profileId,
    profileLabel,
    email,
    password: DEMO_CAPTURE_PASSWORD,
    page,
    magazineSection: magazine?.section,
    magazineHeadline: magazine?.headline,
  };
}

/** Parcours magazine — 12 captures stratégiques, ordre narratif flux patient. */
export const MAGAZINE_TOUR_STEPS: DemoCaptureTourStep[] = [
  step("direction", "Direction", "manager@hospital.demo", PAGE.tableauDeBord, {
    section: "01 — Vue d'ensemble",
    headline: "L'hôpital piloté en temps réel",
  }),
  step("direction", "Direction", "manager@hospital.demo", PAGE.admissions, {
    section: "02 — Parcours patient",
    headline: "De l'accueil à l'orientation",
  }),
  step("direction", "Direction", "manager@hospital.demo", PAGE.patients, {
    section: "02 — Parcours patient",
    headline: "Dossier patient unifié",
  }),
  step("medecin", "Médecin", "doctor@hospital.demo", PAGE.consultations, {
    section: "03 — Clinique",
    headline: "Consultation et file d'attente",
  }),
  step("medecin", "Médecin", "doctor@hospital.demo", PAGE.prescriptions, {
    section: "03 — Clinique",
    headline: "Ordonnance envoyée à la pharmacie",
  }),
  step("pharmacien", "Pharmacien", "pharmacy@hospital.demo", PAGE.ordonnances, {
    section: "04 — Pharmacie",
    headline: "File ordonnances — démo pré-chargée",
  }),
  step("pharmacien", "Pharmacien", "pharmacy@hospital.demo", PAGE.dispensation, {
    section: "04 — Pharmacie",
    headline: "Dispensation et facturation",
  }),
  step("pharmacien", "Pharmacien", "pharmacy@hospital.demo", PAGE.stockArticles, {
    section: "04 — Pharmacie",
    headline: "Stock officine synchronisé",
  }),
  step("direction", "Direction", "manager@hospital.demo", PAGE.assuranceDossiers, {
    section: "05 — Assurance",
    headline: "Accords préalables et tiers payant",
  }),
  step("direction", "Direction", "manager@hospital.demo", PAGE.caisse, {
    section: "06 — Finance",
    headline: "Encaissement caisse intégré",
  }),
  step("medecin", "Médecin", "doctor@hospital.demo", PAGE.laboratoire, {
    section: "07 — Paraclinique",
    headline: "Laboratoire et résultats",
  }),
  step("direction", "Direction", "manager@hospital.demo", PAGE.comptabilite, {
    section: "06 — Finance",
    headline: "Comptabilité hospitalière",
  }),
];

export function buildFullTourSteps(
  profiles: import("./demo-capture.config").DemoCaptureProfile[],
): DemoCaptureTourStep[] {
  return profiles.flatMap((profile) =>
    profile.pages.map((page) => ({
      profileId: profile.id,
      profileLabel: profile.label,
      email: profile.email,
      password: profile.password,
      page,
    })),
  );
}

export function buildMatrixTourSteps(
  profiles: import("./demo-capture.config").DemoCaptureProfile[],
): DemoCaptureTourStep[] {
  const pages = listAllUniquePagesFromProfiles(profiles);
  return profiles.flatMap((profile) =>
    pages.map((page) => ({
      profileId: profile.id,
      profileLabel: profile.label,
      email: profile.email,
      password: profile.password,
      page,
    })),
  );
}

function listAllUniquePagesFromProfiles(
  profiles: import("./demo-capture.config").DemoCaptureProfile[],
): DemoCapturePage[] {
  const seen = new Map<string, DemoCapturePage>();
  for (const profile of profiles) {
    for (const page of profile.pages) {
      if (!seen.has(page.id)) seen.set(page.id, page);
    }
  }
  return [...seen.values()];
}

export function countMatrixTourSteps(
  profiles: import("./demo-capture.config").DemoCaptureProfile[],
): number {
  return profiles.length * listAllUniquePagesFromProfiles(profiles).length;
}

export const MATRIX_TOUR_META = {
  id: "matrix" as const,
  label: "Toutes pages × tous profils",
  shortLabel: "Matrice",
  description:
    "Chaque compte démo visite toutes les pages — comparer les vues et droits par rôle.",
  outputSubdir: "matrix",
};

export const CURRENT_PAGE_TOUR_META = {
  id: "current" as const,
  label: "Page actuelle",
  shortLabel: "Cette page",
  description:
    "Capture uniquement l'écran affiché — compte et URL inchangés. Idéal pour compléter le magazine.",
  outputSubdir: "",
};

export const DEMO_CAPTURE_TOURS: Record<"full" | "magazine", DemoCaptureTourDefinition> = {
  magazine: {
    id: "magazine",
    label: "Magazine décideur",
    shortLabel: "Magazine",
    description:
      "12 captures stratégiques, ordre narratif — flux patient de l'accueil à la caisse.",
    outputSubdir: "magazine",
    steps: MAGAZINE_TOUR_STEPS,
  },
  full: {
    id: "full",
    label: "Banque complète",
    shortLabel: "Complet",
    description: "Toutes les pages par profil (17 captures) — présentation et réseaux.",
    outputSubdir: "",
    steps: [],
  },
};

export function resolveTourDefinition(
  mode: DemoCaptureMode,
  fullProfiles: import("./demo-capture.config").DemoCaptureProfile[],
): DemoCaptureTourDefinition {
  if (mode === "magazine") return DEMO_CAPTURE_TOURS.magazine;
  if (mode === "matrix") {
    return {
      id: "matrix",
      label: MATRIX_TOUR_META.label,
      shortLabel: MATRIX_TOUR_META.shortLabel,
      description: MATRIX_TOUR_META.description,
      outputSubdir: MATRIX_TOUR_META.outputSubdir,
      steps: buildMatrixTourSteps(fullProfiles),
    };
  }
  return {
    ...DEMO_CAPTURE_TOURS.full,
    steps: buildFullTourSteps(fullProfiles),
  };
}

export function buildCaptureFilename(
  mode: DemoCaptureMode,
  profileId: string,
  pageId: string,
  order?: number,
): string {
  if (mode === "magazine" && order != null) {
    const nn = String(order).padStart(2, "0");
    return `magazine-${nn}-${profileId}-${pageId}.png`;
  }
  return `${profileId}__${pageId}.png`;
}

export function countTourSteps(
  mode: DemoCaptureMode,
  fullProfileCount: number,
  profiles?: import("./demo-capture.config").DemoCaptureProfile[],
): number {
  if (mode === "current") return 1;
  if (mode === "magazine") return MAGAZINE_TOUR_STEPS.length;
  if (mode === "matrix" && profiles) return countMatrixTourSteps(profiles);
  return fullProfileCount;
}

export function tourMetaForMode(mode: DemoCaptureMode): {
  label: string;
  shortLabel: string;
  description: string;
} {
  if (mode === "current") return CURRENT_PAGE_TOUR_META;
  if (mode === "matrix") return MATRIX_TOUR_META;
  return DEMO_CAPTURE_TOURS[mode];
}
